import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table";

const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; } };

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/payments"); setPayments(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = payments.reduce((a, p) => a + (p.amount || 0), 0);
  const now = new Date();
  const monthTotal = payments
    .filter((p) => { const d = new Date(p.paid_at); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); })
    .reduce((a, p) => a + (p.amount || 0), 0);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-payments">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl text-brand-ink mb-6">Pagamenti</h1>

        {!loading && payments.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <MetricCard label="Incassato totale" value={eur(total)} accent="green" />
            <MetricCard label="Questo mese" value={eur(monthTotal)} accent="gold" />
            <MetricCard label="N° pagamenti" value={payments.length} />
          </div>
        )}

        <div className="bg-white border border-brand-line rounded-sm p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-brand-muted py-8 justify-center">
              <Loader2 className="animate-spin" size={18} /> Caricamento…
            </div>
          ) : payments.length === 0 ? (
            <p className="text-sm text-brand-muted py-6 text-center">Nessun pagamento registrato.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data</TableHead><TableHead>Cliente</TableHead>
                <TableHead>Importo</TableHead><TableHead>Metodo</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">{fmtDate(p.paid_at)}</TableCell>
                    <TableCell className="text-sm">{p.organization_name || p.buyer_email || "—"}</TableCell>
                    <TableCell className="font-medium">{eur(p.amount)}</TableCell>
                    <TableCell className="text-sm">{p.method === "stripe" ? "Stripe" : "Amministrazione"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}

function MetricCard({ label, value, accent }) {
  const color = accent === "green" ? "text-emerald-700" : accent === "gold" ? "text-brand-gold-deep" : "text-brand-ink";
  return (
    <div className="bg-white border border-brand-line rounded-sm px-4 py-3">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
